import Cookies from 'cookies-js';

// First-touch signup attribution (CAM-601). AdminApp reads this cookie when an account is
// created (ProductAnalytics::SignupSource); the app sets the same cookie for visitors who land
// on app.campsite.bio directly (Campsite.AdminApp/app/javascript/src/utils/first-touch.js).
// The rule is kept identical in both; each repo tests its copy.
//
// Every first visit is recorded, a direct one included, so "direct" is told apart from "never
// captured". A later visit that carries a source (attribution parameters, or a referrer from
// another site) replaces a direct one; otherwise the first visit wins.
//
// Stored: origin + path + the attribution parameters, and the referrer's origin only. Never a
// full URL: a same-site referrer is otherwise a whole app URL, token included (#53 review).
const COOKIE_NAME = 'user_referrer';
const MAX_AGE_SECONDS = 7 * 24 * 60 * 60;
export const ATTRIBUTION_PARAMS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'gclid',
  'fbclid',
  'ttclid',
  'msclkid',
  'ref',
  'referral',
];

// campsite.to is our short-link domain, an alias on the same CloudFront distribution.
function isOwnHost(host) {
  return (
    host === 'campsite.bio' ||
    host.endsWith('.campsite.bio') ||
    host === 'campsite.to' ||
    host.endsWith('.campsite.to')
  );
}

export function sanitizeTouch(href, referrer) {
  let url = '';
  try {
    const u = new URL(href);
    const kept = new URLSearchParams();
    ATTRIBUTION_PARAMS.forEach((p) => {
      if (u.searchParams.has(p)) kept.set(p, u.searchParams.get(p));
    });
    const query = kept.toString();
    url = `${u.origin}${u.pathname}${query ? `?${query}` : ''}`;
  } catch (e) {
    url = '';
  }
  let ref = '';
  try {
    // protocol + host, not `origin`: a non-web scheme (android-app://, the Google and Gmail
    // apps) has an opaque origin, the string "null", which would lose the referrer.
    const r = referrer ? new URL(referrer) : null;
    ref = r ? `${r.protocol}//${r.host}/` : '';
  } catch (e) {
    ref = '';
  }
  return { url, referrer: ref };
}

export function carriesSource(touch) {
  if (!touch) return false;
  try {
    const url = new URL(touch.url);
    if (ATTRIBUTION_PARAMS.some((p) => url.searchParams.has(p))) return true;
    // campsite.bio/referral-<code>: the referral program's link
    if (url.pathname.startsWith('/referral-')) return true;
  } catch (e) {
    // an unparseable landing URL carries nothing
  }
  try {
    return !!touch.referrer && !isOwnHost(new URL(touch.referrer).hostname);
  } catch (e) {
    return false;
  }
}

// The value to write, or null to leave the cookie alone.
export function nextTouch(existing, current) {
  if (!existing) return current;
  if (!carriesSource(existing) && carriesSource(current)) return current;
  return null;
}

function readExisting() {
  const raw = Cookies.get(COOKIE_NAME);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

export default function referrer() {
  /*
   ** Only run on client-side and only in production mode
   */
  if (process.env.NODE_ENV !== 'production' || process.server) return;
  // Production site only: staging.campsite.bio shares the .campsite.bio cookie domain with prod.
  if (
    !['campsite.bio', 'www.campsite.bio'].includes(window.location.hostname)
  ) {
    return;
  }

  const value = nextTouch(readExisting(), {
    ...sanitizeTouch(window.location.href, document.referrer),
    at: new Date().toISOString(),
  });
  if (value) {
    Cookies.set(COOKIE_NAME, JSON.stringify(value), {
      domain: '.campsite.bio',
      expires: MAX_AGE_SECONDS,
    });
  }
}
